/**
 * Create or edit one area (RESP2c).
 *
 * ONE dialog for both, because the fields are the same and the only
 * difference is which request it sends. Two dialogs would be two places
 * to add the next field to, and one of them would eventually be missed.
 *
 * **The edit path sends only what changed.** The server's PATCH treats
 * absent as untouched and present-and-null as cleared, so posting the
 * whole form back would rewrite fields nobody edited — harmless today,
 * and exactly the sort of thing that overwrites a colleague's concurrent
 * change tomorrow.
 *
 * **What is not here matters as much as what is.** No id, no slug, no
 * system code, no activity category: they are immutable server-side and
 * a disabled field showing one would invite the question of how to
 * change it. A rename moves the label and nothing else, which is why
 * there is no warning about "renaming the technical key" — there is no
 * such rename.
 *
 * Icon and colour are pickers over the values the server allows, never
 * free text. A hex field would store a colour that is only right in one
 * theme; a text field for the icon would store a React component name
 * from an untrusted source.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { ResponsiveModal } from "../../../components/ui/responsive-modal";
import UserPicker from "../users/UserPicker";
import UserMultiPicker from "../users/UserMultiPicker";
import {
  createResponsibilityArea,
  patchResponsibilityArea,
} from "../../../api/adminData";

import {
  SELECTABLE_COLOR_TOKENS,
  SELECTABLE_ICONS,
  areaIcon,
  colorTokenName,
} from "./areaPresentation";

const LABEL = "mb-1 block text-[12px] font-medium text-foreground";
const INPUT =
  "w-full rounded-[10px] border border-border bg-background px-3 py-2 text-[13px] text-foreground outline-none focus:ring-2 focus:ring-ring";
const MAX_LABEL = 120;

function toForm(area) {
  return {
    label: area?.label || "",
    description: area?.description || "",
    icon: area?.icon || SELECTABLE_ICONS[0],
    color_token: area?.color_token || SELECTABLE_COLOR_TOKENS[0],
    is_active: area?.is_active !== false,
    default_assigned_to_user_id: area?.default_assigned_to_user_id || "",
    participant_user_ids: Array.isArray(area?.participant_user_ids)
      ? area.participant_user_ids.map(String)
      : [],
    apply_default_on_task_create:
      area?.apply_default_on_task_create !== false,
  };
}

/** Only the keys that actually differ, so a PATCH stays a PATCH. */
function changedFields(form, area) {
  const before = toForm(area);
  const out = {};
  if (form.label.trim() !== before.label) out.label = form.label.trim();
  if ((form.description.trim() || null) !== (before.description || null))
    out.description = form.description.trim() || null;
  if (form.icon !== before.icon) out.icon = form.icon;
  if (form.color_token !== before.color_token)
    out.color_token = form.color_token;
  if (form.is_active !== before.is_active) out.is_active = form.is_active;
  if (
    (form.default_assigned_to_user_id || null) !==
    (before.default_assigned_to_user_id || null)
  )
    out.default_assigned_to_user_id = form.default_assigned_to_user_id || null;
  if (
    JSON.stringify([...form.participant_user_ids].sort()) !==
    JSON.stringify([...before.participant_user_ids].sort())
  )
    out.participant_user_ids = form.participant_user_ids;
  if (
    form.apply_default_on_task_create !== before.apply_default_on_task_create
  )
    out.apply_default_on_task_create = form.apply_default_on_task_create;
  return out;
}

/** A stable German sentence per failure code, never the raw message. */
function messageFor(err) {
  switch (err?.code) {
    case "responsibility_area_label_conflict":
      return "Ein Bereich mit diesem Namen existiert bereits.";
    case "responsibility_area_invalid_icon":
      return "Dieses Symbol ist nicht verfügbar.";
    case "responsibility_area_invalid_color_token":
      return "Diese Farbe ist nicht verfügbar.";
    case "responsibility_area_invalid_assignee":
      return "Die gewählte Person gehört nicht zu dieser Organisation oder ist deaktiviert.";
    case "responsibility_area_slug_generation_failed":
      return "Der Name muss mindestens einen Buchstaben oder eine Ziffer enthalten.";
    default:
      return err?.message || "Der Bereich konnte nicht gespeichert werden.";
  }
}

export default function ResponsibilityAreaFormDialog({
  open,
  area,
  onOpenChange,
  onSaved,
}) {
  const isEdit = Boolean(area?.id);
  const [form, setForm] = useState(() => toForm(area));
  const [saving, setSaving] = useState(false);
  const [labelError, setLabelError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(toForm(area));
      setLabelError(null);
    }
  }, [open, area]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const IconPreview = useMemo(() => areaIcon(form.icon), [form.icon]);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return; // double-submit guard

    const label = form.label.trim();
    if (!label) {
      setLabelError("Name ist erforderlich.");
      return;
    }
    setLabelError(null);
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const changes = changedFields(form, area);
        saved = Object.keys(changes).length
          ? await patchResponsibilityArea(area.id, changes)
          : area;
        toast.success("Bereich gespeichert.");
      } else {
        saved = await createResponsibilityArea({
          label,
          description: form.description.trim() || null,
          icon: form.icon,
          color_token: form.color_token,
          is_active: form.is_active,
          default_assigned_to_user_id: form.default_assigned_to_user_id || null,
          default_participant_user_ids: form.participant_user_ids,
          apply_default_on_task_create: form.apply_default_on_task_create,
        });
        toast.success("Bereich angelegt.");
      }
      onOpenChange(false);
      onSaved?.(saved);
    } catch (err) {
      const text = messageFor(err);
      if (err?.code === "responsibility_area_label_conflict") {
        // At the field, where the fix is — not only in a toast that has
        // to be read before it disappears.
        setLabelError(text);
      }
      toast.error(text);
      // Dialog stays open on failure: closing it would throw away
      // everything the person typed.
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={saving ? () => {} : onOpenChange}
      title={isEdit ? "Bereich bearbeiten" : "Bereich hinzufügen"}
      description={
        isEdit
          ? "Name, Darstellung und Standardpersonen dieses Tätigkeitsbereichs."
          : "Legen Sie einen eigenen Tätigkeitsbereich für Ihre Organisation an."
      }
    >
      {/* RESP2c.1: a flex COLUMN whose body scrolls and whose footer does
          not. `ResponsiveModal` already caps the dialog at 90vh and hands
          its children `min-h-0 flex-1 flex-col` — this form simply never
          used that, so it grew past the cap and the bottom of it,
          including Speichern, was only reachable by zooming the browser
          out. Mirrors TaskFormModal, which has had this shape since
          REPORT-UX1. */}
      <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
        <div>
          <label className={LABEL} htmlFor="ra-label">
            Name
          </label>
          <input
            id="ra-label"
            className={INPUT}
            value={form.label}
            maxLength={MAX_LABEL}
            required
            aria-invalid={labelError ? "true" : undefined}
            aria-describedby={labelError ? "ra-label-error" : undefined}
            onChange={(e) => set({ label: e.target.value })}
          />
          {labelError ? (
            <p
              id="ra-label-error"
              role="alert"
              className="m-0 mt-1 text-[12px] text-destructive"
            >
              {labelError}
            </p>
          ) : null}
        </div>

        <div>
          <label className={LABEL} htmlFor="ra-description">
            Beschreibung
          </label>
          <textarea
            id="ra-description"
            rows={3}
            className={INPUT}
            value={form.description}
            onChange={(e) => set({ description: e.target.value })}
          />
        </div>

        <div>
          <span className={LABEL}>Symbol</span>
          <div
            role="radiogroup"
            aria-label="Symbol"
            className="flex flex-wrap gap-2"
          >
            {SELECTABLE_ICONS.map((key) => {
              const Icon = areaIcon(key);
              const selected = form.icon === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={key}
                  onClick={() => set({ icon: key })}
                  className={
                    "flex h-9 w-9 items-center justify-center rounded-[10px] border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
                    (selected
                      ? "border-violet-500 bg-violet-500/10 text-violet-700 dark:text-violet-300"
                      : "border-border text-muted-foreground hover:bg-muted")
                  }
                >
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <span className={LABEL}>Farbe</span>
          <div
            role="radiogroup"
            aria-label="Farbe"
            className="flex flex-wrap gap-2"
          >
            {SELECTABLE_COLOR_TOKENS.map((token) => {
              const selected = form.color_token === token;
              return (
                <button
                  key={token}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={colorTokenName(token)}
                  onClick={() => set({ color_token: token })}
                  className={
                    "flex h-9 w-9 items-center justify-center rounded-[10px] border " +
                    token +
                    (selected ? " ring-2 ring-violet-500 ring-offset-1" : "")
                  }
                >
                  <IconPreview className="h-4 w-4" strokeWidth={1.75} />
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className={LABEL} htmlFor="ra-main">
            Standard-Hauptverantwortlicher
          </label>
          <UserPicker
            value={form.default_assigned_to_user_id}
            onChange={(id) => set({ default_assigned_to_user_id: id || "" })}
            placeholder="Niemand"
          />
        </div>

        <div>
          <label className={LABEL} htmlFor="ra-participants">
            Standardbeteiligte
          </label>
          <UserMultiPicker
            value={form.participant_user_ids}
            onChange={(ids) => set({ participant_user_ids: ids })}
            excludeIds={
              form.default_assigned_to_user_id
                ? [form.default_assigned_to_user_id]
                : []
            }
          />
        </div>

        <label className="flex items-start gap-2.5">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={form.apply_default_on_task_create}
            onChange={(e) =>
              set({ apply_default_on_task_create: e.target.checked })
            }
          />
          <span>
            <span className="block text-[13px] font-medium text-foreground">
              Bei neuen Aufgaben vorschlagen
            </span>
            <span className="block text-[12px] text-muted-foreground">
              Bereich und Standardpersonen bei neuen Aufgaben vorschlagen
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2.5">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={form.is_active}
            onChange={(e) => set({ is_active: e.target.checked })}
          />
          <span>
            <span className="block text-[13px] font-medium text-foreground">
              Aktiv
            </span>
            <span className="block text-[12px] text-muted-foreground">
              Inaktive Bereiche werden für neue Arbeit nicht mehr angeboten
            </span>
          </span>
        </label>

        </div>

        {/* Outside the scroll region: always reachable, whatever the
            viewport height. `pb-[env(safe-area-inset-bottom)]` keeps it
            clear of the home indicator on a phone. */}
        <div className="mt-3 flex shrink-0 justify-end gap-2 border-t border-border pt-3 pb-[env(safe-area-inset-bottom)]">
          <button
            type="button"
            disabled={saving}
            onClick={() => onOpenChange(false)}
            className="rounded-[10px] border border-border px-4 py-2 text-[13px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-[10px] bg-violet-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
          >
            {saving ? "Wird gespeichert …" : isEdit ? "Speichern" : "Anlegen"}
          </button>
        </div>
      </form>
    </ResponsiveModal>
  );
}
